import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Select,
  Row,
  Col,
  Tag,
  Drawer,
} from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { getTemplateDetails } from '../forms/state/forms.actions';
import AnalyticsChartTypePicker from '../analytics/AnalyticsChartTypePicker';
import { getIdMap } from '../helpers/helpers';
import EChart from '../common/charts/EChart';
import { buildPieOption, buildStackedSteppedOption } from '../common/charts/chartOptions';
import useSearchAnalyticsFields from './useSearchAnalyticsFields';

const MAX_BREAKDOWN_BUCKETS = 20;

const FORMS_BREAKDOWN_OPTS = [
  { label: 'By User', value: 'user' },
  { label: 'By Status', value: 'status' },
  { label: 'By Board Card', value: 'boardCard' },
  { label: 'By Board Status', value: 'boardStatus' },
];

const BOARDS_BREAKDOWN_OPTS = [
  { label: 'By User', value: 'user' },
  { label: 'By Status', value: 'status' },
  { label: 'By Color', value: 'color' },
];

const computeBuckets = (fieldValuesByRecordId, records, fieldId) => {
  const counts = { yes: 0, no: 0, 'n/a': 0 };
  records.forEach((record) => {
    const val = fieldValuesByRecordId[record.id]?.[fieldId];
    if (val && val in counts) {
      counts[val] += 1;
    }
  });
  return counts;
};

const buildPieData = (counts) => {
  const data = [['Response', 'Count']];
  if (counts.yes > 0) data.push(['Yes', counts.yes]);
  if (counts.no > 0) data.push(['No', counts.no]);
  if (counts['n/a'] > 0) data.push(['N/A', counts['n/a']]);
  return data;
};

const ynKeys = { yes: true, no: true, 'n/a': true };

const aggregateByBreakdown = (records, fieldValuesByRecordId, fieldId, breakdownBy, getLabel) => {
  const buckets = new Map();
  records.forEach((record) => {
    const yn = fieldValuesByRecordId[record.id]?.[fieldId];
    if (!yn || !ynKeys[yn]) return;
    const label = getLabel(record, breakdownBy);
    if (!buckets.has(label)) buckets.set(label, { yes: 0, no: 0, 'n/a': 0 });
    buckets.get(label)[yn] += 1;
  });
  return buckets;
};

const sortAndCapBuckets = (buckets) => {
  const entries = [...buckets.entries()].map(([label, c]) => {
    const total = c.yes + c.no + c['n/a'];
    return { label, ...c, total };
  });
  entries.sort((a, b) => b.total - a.total);
  if (entries.length <= MAX_BREAKDOWN_BUCKETS) return entries;
  const head = entries.slice(0, MAX_BREAKDOWN_BUCKETS);
  const tail = entries.slice(MAX_BREAKDOWN_BUCKETS);
  const other = tail.reduce(
    (acc, row) => ({
      label: 'Other',
      yes: acc.yes + row.yes,
      no: acc.no + row.no,
      'n/a': acc['n/a'] + row['n/a'],
      total: acc.total + row.total,
    }),
    {
      label: 'Other',
      yes: 0,
      no: 0,
      'n/a': 0,
      total: 0,
    },
  );
  return [...head, other];
};

/** Pie: one slice per bucket×answer, e.g. Admin-yes, Admin-no */
const buildBreakdownCombinedPieData = (rows) => {
  const data = [['Segment', 'Count']];
  rows.forEach((row) => {
    const {
      label, yes, no, 'n/a': na,
    } = row;
    if (yes > 0) data.push([`${label}-yes`, yes]);
    if (no > 0) data.push([`${label}-no`, no]);
    if (na > 0) data.push([`${label}-n/a`, na]);
  });
  return data;
};

/** Stacked line over date: one series per bucket-answer combination */
const buildBreakdownStackedLineMatrix = (
  records,
  fieldValuesByRecordId,
  fieldId,
  breakdownBy,
  getLabel,
  cappedRows,
) => {
  const allowedLabels = new Set(cappedRows.map((r) => r.label));
  const segmentOrder = [];
  cappedRows.forEach((row) => {
    const {
      label, yes, no, 'n/a': na,
    } = row;
    if (yes > 0) segmentOrder.push(`${label}-yes`);
    if (no > 0) segmentOrder.push(`${label}-no`);
    if (na > 0) segmentOrder.push(`${label}-n/a`);
  });

  if (segmentOrder.length === 0) return null;

  const segmentSet = new Set(segmentOrder);

  const dateMap = {};
  records.forEach((record) => {
    const yn = fieldValuesByRecordId[record.id]?.[fieldId];
    if (!yn || !ynKeys[yn]) return;
    const bucket = String(getLabel(record, breakdownBy) ?? 'Unknown');
    const useLabel = allowedLabels.has(bucket) ? bucket : 'Other';
    const seg = `${useLabel}-${yn}`;
    if (!segmentSet.has(seg)) return;
    const dateKey = record.createdAt
      ? new Date(record.createdAt).toISOString().slice(0, 10)
      : 'Unknown';
    if (!dateMap[dateKey]) dateMap[dateKey] = {};
    dateMap[dateKey][seg] = (dateMap[dateKey][seg] || 0) + 1;
  });

  const dates = Object.keys(dateMap).sort((a, b) => a.localeCompare(b));
  const header = ['Date', ...segmentOrder];
  const rows = [header];
  dates.forEach((d) => {
    const row = [d];
    const m = dateMap[d];
    segmentOrder.forEach((s) => {
      row.push(m[s] || 0);
    });
    rows.push(row);
  });
  return rows;
};

export default function AdvancedSearchAnalyticsDrawer({
  visible,
  onClose,
  activeFilter,
  onFilterChange,
}) {
  const dispatch = useDispatch();

  const [selectedFieldId, setSelectedFieldId] = useState();
  const [chartType, setChartType] = useState('pie');
  const [breakdownBy, setBreakdownBy] = useState('');

  const reduxQuery = useSelector((state) => state.search.selectedQuery);
  const currentQuery = useSelector((state) => state.search.currentQuery);
  const formResults = useSelector((state) => state.search.formResults);
  const boardResults = useSelector((state) => state.search.boardResults);
  const fieldValuesByRecordId = useSelector((state) => state.search.formFieldValues);
  const users = useSelector((state) => state.users.users);
  const boardDetailsMap = useSelector((state) => state.boards.boardDetailsMap);

  const selectedQuery = useMemo(() => currentQuery ?? reduxQuery, [currentQuery, reduxQuery]);
  const analyticsFields = useSearchAnalyticsFields();

  const isBoardsSearch = selectedQuery?.type === 'boards';
  const searchResults = isBoardsSearch ? boardResults : formResults;

  const userMap = useMemo(() => getIdMap(users), [users]);

  const breakdownSelectOptions = useMemo(() => {
    const opts = isBoardsSearch ? BOARDS_BREAKDOWN_OPTS : FORMS_BREAKDOWN_OPTS;
    return [{ value: '', label: 'No breakdown' }, ...opts];
  }, [isBoardsSearch]);

  useEffect(() => {
    if (isBoardsSearch || !selectedQuery?.query?.templateId) return;
    dispatch(getTemplateDetails(selectedQuery?.query?.templateId));
  }, [selectedQuery, isBoardsSearch]);

  useEffect(() => {
    if (!analyticsFields.length) {
      setSelectedFieldId();
      return;
    }
    setSelectedFieldId((prev) => {
      if (prev && analyticsFields.some((f) => f.fieldId === prev)) return prev;
      return analyticsFields[0].fieldId;
    });
  }, [analyticsFields]);

  useEffect(() => {
    setBreakdownBy('');
  }, [selectedFieldId]);

  const fieldOptions = useMemo(
    () => analyticsFields.map(({ fieldId, name }) => ({ value: fieldId, label: name })),
    [analyticsFields],
  );

  const selectedField = useMemo(
    () => analyticsFields.find((f) => f.fieldId === selectedFieldId),
    [analyticsFields, selectedFieldId],
  );

  const fieldId = selectedField?.fieldId;

  const getBreakdownLabel = useCallback((record, key) => {
    if (!key) return '';
    if (isBoardsSearch) {
      if (key === 'status') {
        const statuses = boardDetailsMap[record.boardId]?.statuses ?? [];
        const st = statuses.find((s) => s.id === record.statusId);
        return st?.title || 'Unknown';
      }
      if (key === 'color') {
        const raw = record.color || '#FFFFFFFF';
        return typeof raw === 'string' && raw.length > 7 ? raw.substring(0, 7) : String(raw);
      }
      if (key === 'user') {
        const uid = record.userId;
        return (uid && userMap[uid]?.name) || 'Unassigned';
      }
      return 'Unknown';
    }
    if (key === 'user') {
      const uid = record.userId;
      return (uid && userMap[uid]?.name) || 'Unassigned';
    }
    if (key === 'status') {
      return record.status || 'Unknown';
    }
    if (key === 'boardStatus') {
      const statuses = boardDetailsMap[record.cardBoardId]?.statuses ?? [];
      const st = statuses.find((s) => s.id === record.cardStatusId);
      return st?.title || 'Unknown';
    }
    if (key === 'boardCard') {
      return record.card || 'No card';
    }
    return 'Unknown';
  }, [isBoardsSearch, userMap, boardDetailsMap]);

  const breakdownBuckets = useMemo(() => {
    if (!breakdownBy || !fieldId) return null;
    return aggregateByBreakdown(
      searchResults,
      fieldValuesByRecordId,
      fieldId,
      breakdownBy,
      getBreakdownLabel,
    );
  }, [breakdownBy, fieldId, searchResults, fieldValuesByRecordId, getBreakdownLabel]);

  const breakdownRows = useMemo(() => {
    if (!breakdownBuckets?.size) return [];
    return sortAndCapBuckets(breakdownBuckets);
  }, [breakdownBuckets]);

  const counts = useMemo(
    () => (fieldId
      ? computeBuckets(fieldValuesByRecordId, searchResults, fieldId)
      : { yes: 0, no: 0, 'n/a': 0 }),
    [fieldValuesByRecordId, searchResults, fieldId],
  );

  const pieData = useMemo(() => buildPieData(counts), [counts]);

  const pieSliceMap = useMemo(() => {
    const map = {};
    let idx = 0;
    if (counts.yes > 0) { map[idx] = 'yes'; idx += 1; }
    if (counts.no > 0) { map[idx] = 'no'; idx += 1; }
    if (counts['n/a'] > 0) { map[idx] = 'n/a'; }
    return map;
  }, [counts]);

  const stackedData = useMemo(() => {
    if (chartType !== 'stackedLine' || !fieldId) return null;
    if (breakdownBy) {
      if (!breakdownRows.length) return null;
      return buildBreakdownStackedLineMatrix(
        searchResults,
        fieldValuesByRecordId,
        fieldId,
        breakdownBy,
        getBreakdownLabel,
        breakdownRows,
      );
    }

    const dateMap = {};
    searchResults.forEach((record) => {
      const val = fieldValuesByRecordId[record.id]?.[fieldId];
      if (!val) return;
      const dateKey = record.createdAt
        ? new Date(record.createdAt).toISOString().slice(0, 10)
        : 'Unknown';
      if (!dateMap[dateKey]) dateMap[dateKey] = { yes: 0, no: 0, 'n/a': 0 };
      if (val in dateMap[dateKey]) dateMap[dateKey][val] += 1;
    });

    const sortedDates = Object.keys(dateMap).sort((a, b) => a.localeCompare(b));
    const data = [['Date', 'Yes', 'No', 'N/A']];
    sortedDates.forEach((date) => {
      data.push([date, dateMap[date].yes, dateMap[date].no, dateMap[date]['n/a']]);
    });
    return data;
  }, [
    chartType,
    breakdownBy,
    breakdownRows,
    fieldValuesByRecordId,
    searchResults,
    fieldId,
    getBreakdownLabel,
  ]);

  const breakdownPieData = useMemo(() => {
    if (!breakdownBy || breakdownRows.length === 0) return null;
    return buildBreakdownCombinedPieData(breakdownRows);
  }, [breakdownBy, breakdownRows]);

  const hasBreakdown = Boolean(breakdownBy);

  const chartData = useMemo(() => {
    if (hasBreakdown && chartType === 'pie') return breakdownPieData;
    return chartType === 'pie' ? pieData : stackedData;
  }, [hasBreakdown, chartType, breakdownPieData, pieData, stackedData]);

  const chartOption = useMemo(() => {
    if (!chartData || chartData.length < 2) return null;
    if (chartType === 'pie') {
      return buildPieOption({
        slices: chartData.slice(1).map((row) => ({
          name: row[0],
          value: row[1],
        })),
      });
    }
    const [header, ...rows] = chartData;
    return buildStackedSteppedOption({
      categories: rows.map((row) => row[0]),
      series: header.slice(1).map((name, seriesIndex) => ({
        name,
        values: rows.map((row) => row[seriesIndex + 1] ?? 0),
      })),
    });
  }, [chartData, chartType]);

  const onChartClick = useCallback((params) => {
    if (params?.componentType === 'series' && chartType === 'pie' && !breakdownBy) {
      const value = pieSliceMap[params.dataIndex];
      if (value && fieldId) {
        if (activeFilter?.fieldId === fieldId && activeFilter?.value === value) {
          onFilterChange(null);
        } else {
          onFilterChange({ fieldId, value });
        }
      }
    }
  }, [chartType, breakdownBy, pieSliceMap, fieldId, activeFilter, onFilterChange]);

  const onEvents = useMemo(() => ({
    click: onChartClick,
  }), [onChartClick]);

  const hasActiveFilter = Boolean(activeFilter);

  const activeFilterField = useMemo(
    () => analyticsFields.find((f) => f.fieldId === activeFilter?.fieldId),
    [analyticsFields, activeFilter],
  );

  const activeFilterValueLabel = useMemo(() => {
    const { value } = activeFilter ?? {};
    if (!value) return '';
    const stringValue = String(value);
    return stringValue === 'n/a' ? 'N/A' : `${stringValue.charAt(0).toUpperCase()}${stringValue.slice(1).toLowerCase()}`;
  }, [activeFilter]);

  const chartHasData = chartData && chartData.length > 1;

  return (
    <Drawer
      title="Search Analytics"
      visible={visible}
      onClose={onClose}
      width={1100}
    >
      <Row gutter={16} align="middle" wrap>
        <Col>
          {fieldOptions.length > 0 && (
            <Select
              value={selectedFieldId}
              onChange={setSelectedFieldId}
              options={fieldOptions}
              style={{ minWidth: 280 }}
              showSearch
              optionFilterProp="label"
            />
          )}
        </Col>
        <Col>
          <AnalyticsChartTypePicker
            selectedChartType={chartType}
            onSelect={setChartType}
            fieldType="yes-no"
          />
        </Col>
        <Col>
          <Select
            allowClear
            placeholder="Breakdown by"
            value={breakdownBy}
            onChange={setBreakdownBy}
            options={breakdownSelectOptions}
            style={{ minWidth: 220 }}
            showSearch
            optionFilterProp="label"
          />
        </Col>
        <Col>
          <span style={{ color: '#888' }}>
            ({counts.yes + counts.no + counts['n/a']} responses
            {counts.yes > 0 && ` · ${counts.yes} yes`}
            {counts.no > 0 && ` · ${counts.no} no`}
            {counts['n/a'] > 0 && ` · ${counts['n/a']} n/a`}
            )
          </span>
        </Col>
      </Row>
      {hasActiveFilter && (
        <Row style={{ marginTop: 12, marginBottom: 8, width: '100%' }}>
          <Col span={24}>
            <Tag
              color="blue"
              closable
              onClose={() => onFilterChange(null)}
            >
              Filtered:
              {' '}
              <strong>{activeFilterField?.name}</strong>
              {': '}
              {activeFilterValueLabel}
            </Tag>
          </Col>
        </Row>
      )}
      {chartHasData && (
        <div
          style={{
            marginTop: 16,
            marginBottom: 16,
            border: '1px solid #C3D2E0',
            borderRadius: 12,
            padding: 14,
            backgroundColor: '#fff',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
          }}
        >
          <div style={{ width: '100%', maxWidth: 960 }}>
            <EChart
              option={chartOption}
              onEvents={onEvents}
              style={{ width: '100%', height: 420 }}
            />
          </div>
        </div>
      )}
    </Drawer>
  );
}

AdvancedSearchAnalyticsDrawer.propTypes = {
  activeFilter: PropTypes.shape({
    fieldId: PropTypes.string,
    value: PropTypes.string,
  }),
  onFilterChange: PropTypes.func.isRequired,
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

AdvancedSearchAnalyticsDrawer.defaultProps = {
  activeFilter: null,
};
